import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Divider,
  FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Paper,
  Select, Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, ArrowBack as BackIcon, Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import menuItemService from '../../../../services/menuItemService';
import menuSectionService from '../../../../services/menuSectionService';
import menuFormatService from '../../../../services/menuFormatService';
import menuItemFormatService from '../../../../services/menuItemFormatService';
import inventoryItemService from '../../../../services/inventoryItemService';
import type { InventoryItemWithBalance } from '../../../../services/inventoryItemService';
import WineSearchBox from '../../../../components/venue/WineSearchBox';
import type { WineSearchResult } from '../../../../components/venue/WineSearchBox';
import CreateWineModal from '../../../../components/venue/CreateWineModal';
import FormatExecutionBlock from '../../../../components/venue/FormatExecutionBlock';
import type { Wine } from '../../../../services/wineService';
import type {
  MenuFormat, MenuItemFormat as MenuItemFormatDoc, MenuSection, MenuVisibility,
  MenuWineDetails, MenuWineScoreSource,
} from '../../../../types/menu';

const SCORE_SOURCES: MenuWineScoreSource[] = ['JS', 'WA', 'WS', 'AV', 'RP'];
const SCORE_SOURCE_LABELS: Record<MenuWineScoreSource, string> = {
  JS: 'James Suckling',
  WA: 'Wine Advocate',
  WS: 'Wine Spectator',
  AV: 'Antonio Galloni / Vinous',
  RP: 'Robert Parker',
};

interface ScoreRow {
  source: MenuWineScoreSource | '';
  score: string;
}

interface FormData {
  basePrice: string;
  currency: string;
  sectionId: string;
  visibility: MenuVisibility;
  notes: string;
  externalUrl: string;
  needsReview: boolean;
  position: string;
  bottleSize: string;
  binNumber: string;
}

interface FormatRow {
  /** Server id when persisted, undefined when this row is new. */
  _id?: string;
  formatId: string;
  /** Free-text price to support typing in progress. Validated on save. */
  price: string;
  visibility: MenuVisibility;
  inventoryItem: InventoryItemWithBalance | null;
  consumptionRecipeId: string | null;
}

const emptyFormData = (defaultCurrency: string): FormData => ({
  basePrice: '',
  currency: defaultCurrency,
  sectionId: '',
  visibility: 'LIVE',
  notes: '',
  externalUrl: '',
  needsReview: false,
  position: '',
  bottleSize: '',
  binNumber: '',
});

/**
 * Map a freshly-created Wine (from CreateWineModal) to the lightweight
 * WineSearchResult shape that WineSearchBox / this form consume. Mirrors the
 * internal helper inside WineSearchBox; kept local rather than exporting that
 * one to keep WineSearchBox's surface narrow.
 */
const wineToSearchResult = (wine: Wine): WineSearchResult => ({
  _id: wine._id,
  name: wine.name,
  displayVintage: wine.displayVintage,
  vintageType: wine.vintageType,
  wineDisplay: {
    producerName: wine.wineName?.producer?.name,
    regionName: wine.wineName?.region?.name,
    color: wine.wineName?.color,
  },
});

/**
 * Wine item create / edit form (Phase 4.B.b).
 *
 * - kind is hardcoded to 'WINE' in the create payload. Never sent on update
 *   so the backend's KIND_CHANGE_NOT_ALLOWED guard stays untriggered.
 * - In edit mode the wine selection is read-only — changing wineId is not
 *   permitted in this phase. wineDisplay is re-sent verbatim from the loaded
 *   document so the backend's WINE_DISPLAY_REQUIRED guard passes.
 * - displaySnapshot is derived from the selected wine: name + producer/region
 *   subtitle. The admin never types these manually.
 * - Per-format pricing reuses the same diff-save pattern as MenuItemFormCocktail.
 */
const MenuItemFormWine: React.FC = () => {
  const navigate = useNavigate();
  const { menuId, itemId } = useParams<{ menuId: string; itemId?: string }>();
  const theme = useTheme();
  const { venueId, defaultCurrency } = useVenue();
  const isEdit = Boolean(itemId);
  const backPath = `/admin/venues/${venueId}/menus/${menuId}`;

  // Wine selection — drives identity, displaySnapshot, and wineDetails.
  const [selectedWine, setSelectedWine] = useState<WineSearchResult | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalQuery, setCreateModalQuery] = useState('');

  // Editable form fields.
  const [formData, setFormData] = useState<FormData>(emptyFormData(defaultCurrency));
  const [errors, setErrors] = useState<Partial<Record<keyof FormData | 'wine' | 'scores', string>>>({});
  const [loading, setLoading] = useState(isEdit);
  const [loadFailed, setLoadFailed] = useState(false);
  const [wrongKind, setWrongKind] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  // Sections + formats lookups.
  const [sections, setSections] = useState<MenuSection[]>([]);
  const [formats, setFormats] = useState<MenuFormat[]>([]);

  // Item-level inventory link (loaded + round-tripped; control hidden in 7.C.1).
  const [linkedItem, setLinkedItem] = useState<InventoryItemWithBalance | null>(null);

  // Wine scores rows. Stored as a UI-friendly array; empty rows are dropped on save.
  const [scoreRows, setScoreRows] = useState<ScoreRow[]>([]);

  // Per-item format pricing.
  const [formatRows, setFormatRows] = useState<FormatRow[]>([]);
  const [originalFormatRows, setOriginalFormatRows] = useState<FormatRow[]>([]);

  // In edit mode we hold the originally-loaded wineDetails so update payloads
  // can re-send wineDisplay verbatim (backend requires it for kind=WINE updates
  // that touch wineDetails).
  const [originalWineDetails, setOriginalWineDetails] = useState<MenuWineDetails | null>(null);

  // ── Phase 7.A.2c — execution-item link/create support ─────────────────────
  // Human-readable item name used to pre-fill a created execution item's name.
  const menuItemName = useMemo(() => {
    const name = selectedWine?.name ?? originalWineDetails?.wineDisplay?.name ?? '';
    const vintage = selectedWine?.displayVintage ?? originalWineDetails?.wineDisplay?.displayVintage ?? '';
    return [name, vintage].filter(Boolean).join(' ').trim();
  }, [selectedWine, originalWineDetails]);

  // Suggested execution-item name for a serving-size row: "<item> — <format>".
  const suggestedExecutionName = useCallback(
    (formatId: string): string => {
      const f = formats.find(x => x._id === formatId);
      const label = f?.name ?? '';
      return [menuItemName, label].filter(Boolean).join(' — ');
    },
    [formats, menuItemName],
  );

  // After a link/create from the execution block, sync BOTH formatRows and
  // originalFormatRows for that row so the form's diff-save never overwrites
  // inventoryItemId with a stale value.
  const handleLinkedItemChange = useCallback(
    (formatRowId: string, item: InventoryItemWithBalance | null) => {
      setFormatRows(rows => rows.map(r => (r._id === formatRowId ? { ...r, inventoryItem: item } : r)));
      setOriginalFormatRows(rows => rows.map(r => (r._id === formatRowId ? { ...r, inventoryItem: item } : r)));
    },
    [],
  );

  // ── Load sections / formats ───────────────────────────────────────────────
  useEffect(() => {
    if (!menuId) return;
    menuSectionService.getSections(venueId, menuId)
      .then(setSections)
      .catch(() => { /* non-blocking */ });
    menuFormatService.getFormats(venueId, menuId)
      .then(setFormats)
      .catch(() => { /* non-blocking */ });
  }, [venueId, menuId]);

  // ── Load existing item in edit mode ───────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !menuId || !itemId) return;
    setLoading(true);
    menuItemService.getItem(venueId, menuId, itemId)
      .then(item => {
        if (item.kind !== 'WINE') {
          setWrongKind(true);
          return;
        }
        const wd = item.wineDetails;
        if (wd) {
          // Seed selectedWine so WineSearchBox renders the locked label.
          setSelectedWine({
            _id: wd.wineId,
            name: wd.wineDisplay?.name ?? '(unknown)',
            displayVintage: wd.wineDisplay?.displayVintage ?? '',
            vintageType: 'single_vintage',
            wineDisplay: {
              producerName: wd.wineDisplay?.producerName,
              regionName: wd.wineDisplay?.regionName,
              color: wd.wineDisplay?.color,
            },
          });
          setOriginalWineDetails(wd);
          setScoreRows(
            Array.isArray(wd.scores)
              ? wd.scores.map(s => ({ source: s.source, score: String(s.score) }))
              : [],
          );
        }
        setFormData({
          basePrice: item.basePrice != null ? String(item.basePrice) : '',
          currency: item.currency ?? defaultCurrency,
          sectionId: item.sectionId ?? '',
          visibility: item.visibility,
          notes: item.notes ?? '',
          externalUrl: item.externalUrl ?? '',
          needsReview: Boolean(item.needsReview),
          position: item.position != null ? String(item.position) : '',
          bottleSize: wd?.bottleSize != null ? String(wd.bottleSize) : '',
          binNumber: wd?.binNumber ?? '',
        });
        if (item.inventoryItemId) {
          inventoryItemService.getItem(venueId, item.inventoryItemId)
            .then(inv => setLinkedItem(inv as unknown as InventoryItemWithBalance))
            .catch(() => { /* non-blocking */ });
        }
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, [isEdit, menuId, itemId, venueId, defaultCurrency]);

  // ── Load existing per-item format pricings in edit mode ───────────────────
  useEffect(() => {
    if (!isEdit || !itemId) return;
    menuItemFormatService.getItemFormats(venueId, itemId)
      .then(async (rows: MenuItemFormatDoc[]) => {
        const mapped: FormatRow[] = await Promise.all(rows.map(async r => {
          let invItem: InventoryItemWithBalance | null = null;
          if (r.inventoryItemId) {
            try {
              invItem = (await inventoryItemService.getItem(venueId, r.inventoryItemId)) as unknown as InventoryItemWithBalance;
            } catch { /* non-blocking */ }
          }
          return {
            _id: r._id,
            formatId: typeof r.formatId === 'string' ? r.formatId : (r.formatId as any)._id,
            price: String(r.price),
            visibility: r.visibility,
            inventoryItem: invItem,
            consumptionRecipeId: r.consumptionRecipeId ?? null,
          };
        }));
        setFormatRows(mapped);
        setOriginalFormatRows(mapped.map(r => ({ ...r })));
      })
      .catch(() => { /* non-blocking */ });
  }, [isEdit, itemId, venueId]);

  // ── Bottle size lock: when the linked item has sizeValue, mirror it and
  // disable manual editing so the two sources can't disagree. ──────────────
  const bottleSizeLocked = linkedItem?.sizeValue != null;
  useEffect(() => {
    if (linkedItem?.sizeValue != null) {
      setFormData(prev => ({ ...prev, bottleSize: String(linkedItem.sizeValue) }));
    }
  }, [linkedItem]);

  // ── Generic field change ──────────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  // ── Score row helpers ─────────────────────────────────────────────────────
  const addScoreRow = () => {
    setScoreRows(rows => [...rows, { source: '', score: '' }]);
    setErrors(prev => ({ ...prev, scores: undefined }));
  };
  const updateScoreRow = (idx: number, patch: Partial<ScoreRow>) => {
    setScoreRows(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
    setErrors(prev => ({ ...prev, scores: undefined }));
  };
  const removeScoreRow = (idx: number) => {
    setScoreRows(rows => rows.filter((_, i) => i !== idx));
    setErrors(prev => ({ ...prev, scores: undefined }));
  };

  // Sources already used elsewhere — for disabling duplicates in row select.
  const usedSources = (currentIdx: number): Set<MenuWineScoreSource> => {
    const set = new Set<MenuWineScoreSource>();
    scoreRows.forEach((r, i) => {
      if (i !== currentIdx && r.source) set.add(r.source);
    });
    return set;
  };

  // ── Format row helpers ────────────────────────────────────────────────────
  const addFormatRow = () => {
    setFormatRows(rows => [
      ...rows,
      { formatId: '', price: '', visibility: 'LIVE', inventoryItem: null, consumptionRecipeId: null },
    ]);
  };
  const updateFormatRow = (idx: number, patch: Partial<FormatRow>) => {
    setFormatRows(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };
  const removeFormatRow = (idx: number) => {
    setFormatRows(rows => rows.filter((_, i) => i !== idx));
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData | 'wine' | 'scores', string>> = {};

    if (!selectedWine) {
      newErrors.wine = 'Pick a wine.';
    }
    if (formData.basePrice && (isNaN(parseFloat(formData.basePrice)) || parseFloat(formData.basePrice) < 0)) {
      newErrors.basePrice = 'Must be a non-negative number.';
    }
    if (formData.position && isNaN(parseInt(formData.position, 10))) {
      newErrors.position = 'Must be a whole number.';
    }
    if (formData.bottleSize) {
      const bs = parseFloat(formData.bottleSize);
      if (isNaN(bs) || bs <= 0) {
        newErrors.bottleSize = 'Must be a positive number (ml).';
      }
    }

    // Scores: each row needs both source and valid score, no duplicate sources.
    const seenSources = new Set<string>();
    for (const row of scoreRows) {
      if (!row.source || !row.score) {
        newErrors.scores = 'Every score needs a source and a number 50–100.';
        break;
      }
      if (seenSources.has(row.source)) {
        newErrors.scores = 'Each score source can appear at most once.';
        break;
      }
      seenSources.add(row.source);
      const n = parseFloat(row.score);
      if (isNaN(n) || n < 50 || n > 100) {
        newErrors.scores = 'Scores must be numbers between 50 and 100.';
        break;
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    // Format pricing rows — same rules as cocktail form.
    const seenFormats = new Set<string>();
    for (const row of formatRows) {
      if (!row.formatId) { setApiError('Every serving size needs a format selected.'); return false; }
      if (seenFormats.has(row.formatId)) {
        setApiError('A serving size can be priced only once per item.');
        return false;
      }
      seenFormats.add(row.formatId);
      const priceNum = parseFloat(row.price);
      if (!row.price || isNaN(priceNum) || priceNum < 0) {
        setApiError('Every serving size needs a non-negative price.');
        return false;
      }
    }
    return true;
  };

  // ── Build wineDetails payload from current state ──────────────────────────
  const buildWineDetailsPayload = (): MenuWineDetails | null => {
    if (!selectedWine) return null;
    const bottleSize = formData.bottleSize ? parseFloat(formData.bottleSize) : undefined;
    const binNumber = formData.binNumber.trim() || undefined;
    const scores = scoreRows
      .filter(r => r.source && r.score)
      .map(r => ({ source: r.source as MenuWineScoreSource, score: parseFloat(r.score) }));

    // On edit, prefer the originally-loaded wineDisplay so we don't accidentally
    // overwrite snapshot fields the search-result projection didn't carry.
    const wineDisplay = isEdit && originalWineDetails?.wineDisplay
      ? originalWineDetails.wineDisplay
      : {
          name: selectedWine.name,
          displayVintage: selectedWine.displayVintage,
          producerName: selectedWine.wineDisplay?.producerName ?? '',
          regionName: selectedWine.wineDisplay?.regionName ?? '',
          color: selectedWine.wineDisplay?.color ?? '',
        };

    return {
      wineId: selectedWine._id,
      wineDisplay,
      ...(scores.length > 0 && { scores }),
      ...(bottleSize !== undefined && { bottleSize }),
      ...(binNumber !== undefined && { binNumber }),
    };
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    if (!validateForm()) return;
    if (!menuId) return;
    const wineDetails = buildWineDetailsPayload();
    if (!wineDetails) return;

    setSaving(true);
    try {
      const basePrice = formData.basePrice ? parseFloat(formData.basePrice) : undefined;
      const position = formData.position ? parseInt(formData.position, 10) : 0;

      // displaySnapshot is derived from the wine — name + producer · region.
      const subtitleBits = [
        wineDetails.wineDisplay.producerName,
        wineDetails.wineDisplay.regionName,
      ].filter((s): s is string => Boolean(s && s.trim()));
      const displaySnapshot = {
        name: wineDetails.wineDisplay.name,
        ...(subtitleBits.length > 0 && { subtitle: subtitleBits.join(' · ') }),
      };

      let savedItemId = itemId;
      if (isEdit && itemId) {
        // Update — never send `kind` (kind is immutable post-create).
        await menuItemService.updateItem(venueId, menuId, itemId, {
          displaySnapshot,
          basePrice,
          currency: formData.currency.trim() || undefined,
          sectionId: formData.sectionId === '' ? null : formData.sectionId,
          visibility: formData.visibility,
          notes: formData.notes.trim() || undefined,
          externalUrl: formData.externalUrl.trim() || undefined,
          needsReview: formData.needsReview,
          position,
          inventoryItemId: linkedItem ? linkedItem._id : null,
          wineDetails,
        });
      } else {
        const created = await menuItemService.createItem(venueId, menuId, {
          kind: 'WINE',
          displaySnapshot,
          basePrice,
          currency: formData.currency.trim() || undefined,
          sectionId: formData.sectionId === '' ? null : formData.sectionId,
          visibility: formData.visibility,
          source: 'MANUAL',
          notes: formData.notes.trim() || undefined,
          externalUrl: formData.externalUrl.trim() || undefined,
          needsReview: formData.needsReview,
          position,
          inventoryItemId: linkedItem ? linkedItem._id : null,
          wineDetails,
        } as Parameters<typeof menuItemService.createItem>[2]);
        savedItemId = created._id;
      }

      // ── Diff and persist per-item format pricings ─────────────────────────
      if (savedItemId) {
        const originalById = new Map(originalFormatRows.filter(r => r._id).map(r => [r._id!, r]));
        const currentIds = new Set(formatRows.filter(r => r._id).map(r => r._id!));

        for (const orig of originalFormatRows) {
          if (orig._id && !currentIds.has(orig._id)) {
            await menuItemFormatService.deleteItemFormat(venueId, savedItemId, orig._id);
          }
        }
        for (const row of formatRows) {
          const priceNum = parseFloat(row.price);
          const rowInvId = row.inventoryItem ? row.inventoryItem._id : null;
          if (!row._id) {
            await menuItemFormatService.createItemFormat(venueId, savedItemId, {
              formatId: row.formatId,
              price: priceNum,
              visibility: row.visibility,
              inventoryItemId: rowInvId,
              consumptionRecipeId: row.consumptionRecipeId,
            });
          } else {
            const orig = originalById.get(row._id);
            const origPrice = orig ? parseFloat(orig.price) : NaN;
            const origInvId = orig?.inventoryItem ? orig.inventoryItem._id : null;
            const origRecipeId = orig?.consumptionRecipeId ?? null;
            const origVisibility = orig?.visibility;
            const changed =
              origPrice !== priceNum ||
              origInvId !== rowInvId ||
              origRecipeId !== row.consumptionRecipeId ||
              origVisibility !== row.visibility;
            if (changed) {
              await menuItemFormatService.updateItemFormat(venueId, savedItemId, row._id, {
                price: priceNum,
                visibility: row.visibility,
                inventoryItemId: rowInvId,
                consumptionRecipeId: row.consumptionRecipeId,
              });
            }
          }
        }
      }
      navigate(backPath);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      const backendMessage = err?.response?.data?.message;
      switch (code) {
        case 'KIND_NOT_SUPPORTED_YET':
          setApiError('Wine items are not enabled on this menu yet.');
          break;
        case 'KIND_CHANGE_NOT_ALLOWED':
          setApiError('The kind of an existing item cannot be changed.');
          break;
        case 'WINE_DETAILS_REQUIRED':
        case 'WINE_DISPLAY_REQUIRED':
          setErrors(prev => ({ ...prev, wine: 'Wine selection and display details are required.' }));
          break;
        case 'WINE_ID_REQUIRED':
        case 'WINE_NOT_FOUND':
          setErrors(prev => ({ ...prev, wine: 'Pick a valid wine.' }));
          break;
        case 'WINE_SCORE_INVALID':
          setErrors(prev => ({ ...prev, scores: backendMessage ?? 'One of the scores is invalid (50–100 required).' }));
          break;
        case 'WINE_SCORE_DUPLICATE':
          setErrors(prev => ({ ...prev, scores: 'Each score source can appear at most once.' }));
          break;
        case 'SECTION_NOT_VALID':
          setApiError('The selected section is no longer active on this menu.');
          break;
        default:
          setApiError(backendMessage ?? 'Failed to save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Early returns ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadFailed) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary, alignSelf: 'flex-start' }}>
          Menu
        </Button>
        <Alert severity="error">
          Could not load this wine item. It may have been removed. Go back and try again.
        </Alert>
      </Box>
    );
  }

  if (wrongKind) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary, alignSelf: 'flex-start' }}>
          Menu
        </Button>
        <Alert severity="error">
          This item is not a wine item — open it from its own row to edit.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Menu
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>{isEdit ? 'Edit Wine' : 'Add Wine'}</Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {apiError && <Alert severity="error" sx={{ borderRadius: 1 }}>{apiError}</Alert>}

          {/* Wine selection — required, locked in edit mode. */}
          <Box>
            <WineSearchBox
              value={selectedWine}
              onChange={wine => {
                setSelectedWine(wine);
                setErrors(prev => ({ ...prev, wine: undefined }));
              }}
              onCreateNew={query => {
                setCreateModalQuery(query);
                setCreateModalOpen(true);
              }}
              error={Boolean(errors.wine)}
              helperText={
                errors.wine
                ?? (isEdit ? 'Wine cannot be changed in this phase.' : 'Pick an existing wine or create a new one.')
              }
              disabled={isEdit}
              venueId={venueId}
              label="Wine"
            />
            {selectedWine && (
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                {selectedWine.wineDisplay?.producerName && (
                  <Chip size="small" label={selectedWine.wineDisplay.producerName} />
                )}
                {selectedWine.wineDisplay?.regionName && (
                  <Chip size="small" label={selectedWine.wineDisplay.regionName} variant="outlined" />
                )}
                {selectedWine.displayVintage && (
                  <Chip size="small" label={selectedWine.displayVintage} sx={{ fontFamily: 'monospace' }} />
                )}
                {selectedWine.wineDisplay?.color && (
                  <Chip size="small" label={selectedWine.wineDisplay.color} variant="outlined" />
                )}
              </Stack>
            )}
          </Box>

          {/* Bottle Price + currency + position */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Bottle Price"
              name="basePrice"
              value={formData.basePrice}
              onChange={handleInputChange}
              error={Boolean(errors.basePrice)}
              helperText={errors.basePrice ?? 'Optional. Default price when no per-format price is set.'}
              type="number"
              sx={{ flex: 2, minWidth: 140 }}
              slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
            />
            <TextField
              label="Currency"
              name="currency"
              value={formData.currency}
              onChange={handleInputChange}
              sx={{ width: 130 }}
              placeholder={defaultCurrency || 'GBP'}
              helperText={defaultCurrency ? `Default: ${defaultCurrency}` : undefined}
              slotProps={{ htmlInput: { maxLength: 3, style: { textTransform: 'uppercase' } } }}
            />
            <TextField
              label="Position"
              name="position"
              value={formData.position}
              onChange={handleInputChange}
              error={Boolean(errors.position)}
              helperText={errors.position ?? 'Lower = earlier'}
              type="number"
              sx={{ width: 120 }}
              slotProps={{ htmlInput: { min: 0, step: 1 } }}
            />
          </Box>

          {/* Section + Visibility */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 240, flex: 1 }}>
              <InputLabel>Section</InputLabel>
              <Select
                value={formData.sectionId}
                label="Section"
                onChange={e => setFormData(prev => ({ ...prev, sectionId: e.target.value }))}
              >
                <MenuItem value=""><em>Unsectioned</em></MenuItem>
                {sections.map(s => (
                  <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 240, flex: 1 }}>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={formData.visibility}
                label="Visibility"
                onChange={e => setFormData(prev => ({ ...prev, visibility: e.target.value as MenuVisibility }))}
              >
                <MenuItem value="LIVE">Live — visible on menu</MenuItem>
                <MenuItem value="HIDDEN">Hidden — not shown</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Phase 7.C.1 — the item-level inventory link control is hidden from the
              normal operator UI. Per-serving "Stock & recipe" is now the canonical
              execution link. Any existing linkedItem (MenuItem.inventoryItemId) is
              still loaded and round-tripped in the save payload below. */}

          {/* Bottle size + bin number */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Bottle Size (ml)"
              name="bottleSize"
              value={formData.bottleSize}
              onChange={handleInputChange}
              error={Boolean(errors.bottleSize)}
              helperText={
                errors.bottleSize
                ?? (bottleSizeLocked
                  ? 'Locked to linked inventory item size.'
                  : 'Optional. Defaults from linked inventory item when one is selected.')
              }
              disabled={bottleSizeLocked}
              type="number"
              sx={{ flex: 1, minWidth: 200 }}
              slotProps={{ htmlInput: { min: 0, step: '1' } }}
            />
            <TextField
              label="Bin Number"
              name="binNumber"
              value={formData.binNumber}
              onChange={handleInputChange}
              placeholder="e.g. 47"
              helperText="Optional. Staff-only — never shown on the customer menu."
              sx={{ flex: 1, minWidth: 200 }}
              slotProps={{ htmlInput: { maxLength: 50 } }}
            />
          </Box>

          {/* Scores */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>Scores</Typography>
                <Typography variant="caption" color="text.secondary">
                  Optional. 50–100, one entry per source.
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addScoreRow}
                disabled={scoreRows.length >= SCORE_SOURCES.length}
                sx={{ textTransform: 'none' }}
              >
                Add score
              </Button>
            </Box>
            {errors.scores && (
              <Alert severity="error" sx={{ borderRadius: 1, mb: 1 }}>{errors.scores}</Alert>
            )}
            {scoreRows.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No scores set.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {scoreRows.map((row, idx) => {
                  const used = usedSources(idx);
                  return (
                    <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <FormControl size="small" sx={{ flex: 1, minWidth: 200 }}>
                        <InputLabel>Source</InputLabel>
                        <Select
                          value={row.source}
                          label="Source"
                          onChange={e => updateScoreRow(idx, { source: e.target.value as MenuWineScoreSource })}
                        >
                          {SCORE_SOURCES.map(src => (
                            <MenuItem
                              key={src}
                              value={src}
                              disabled={used.has(src) && row.source !== src}
                            >
                              {src} — {SCORE_SOURCE_LABELS[src]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="Score"
                        value={row.score}
                        onChange={e => updateScoreRow(idx, { score: e.target.value })}
                        type="number"
                        sx={{ width: 110 }}
                        slotProps={{ htmlInput: { min: 50, max: 100, step: 1 } }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeScoreRow(idx)}
                        aria-label="Remove score"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* Notes + External URL */}
          <TextField
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={2}
            placeholder="Tasting notes or list copy (optional)"
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
          <TextField
            label="External link"
            name="externalUrl"
            value={formData.externalUrl}
            onChange={handleInputChange}
            fullWidth
            placeholder="https://..."
            helperText="Optional. Shown as 'View details' on the customer view."
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.needsReview}
                onChange={e => setFormData(prev => ({ ...prev, needsReview: e.target.checked }))}
              />
            }
            label="Needs review"
          />

          {/* Phase 4.L — same restaurant-language as the cocktail form.
              Architecture note (developer-only): per-serving-size rows map
              1:1 to MenuItemFormat documents; recipe links live on each row
              rather than on the item itself. */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>Serving sizes &amp; stock</Typography>
                <Typography variant="caption" color="text.secondary">
                  Optional. Each serving size has its own selling price and its own stock &amp; recipe setup. Manage the available serving sizes in the Serving sizes tab.
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                disabled={formats.length === 0 || formatRows.length >= formats.length}
                onClick={addFormatRow}
                sx={{ textTransform: 'none' }}
              >
                Add serving size
              </Button>
            </Box>
            {formats.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                  Create serving sizes for this menu first.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Serving sizes define options like Bottle, 150ml glass, Plate or Bowl. Add them in this menu&apos;s Serving sizes tab, then come back to set prices and stock &amp; recipe.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate(`${backPath}?tab=formats`)}
                  sx={{ textTransform: 'none' }}
                >
                  Go to Serving sizes
                </Button>
              </Alert>
            ) : formatRows.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No serving sizes configured yet. Click "Add serving size" to add one.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {formatRows.map((row, idx) => {
                  const usedElsewhere = new Set(
                    formatRows.filter((_, i) => i !== idx).map(r => r.formatId).filter(Boolean),
                  );
                  return (
                    <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      {/* Line 1: serving size + selling price + visibility + delete */}
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
                        <FormControl size="small" sx={{ flex: 2, minWidth: 180 }}>
                          <InputLabel>Serving size</InputLabel>
                          <Select
                            value={row.formatId}
                            label="Serving size"
                            onChange={e => updateFormatRow(idx, { formatId: e.target.value })}
                          >
                            {formats.map(f => (
                              <MenuItem
                                key={f._id}
                                value={f._id}
                                disabled={usedElsewhere.has(f._id) && row.formatId !== f._id}
                              >
                                {f.name}
                                {f.formatKind === 'VOLUME' && f.volumeMl != null && ` · ${f.volumeMl} ml`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          size="small"
                          label="Selling price"
                          value={row.price}
                          onChange={e => updateFormatRow(idx, { price: e.target.value })}
                          type="number"
                          sx={{ width: 130 }}
                          slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                        />
                        <FormControl size="small" sx={{ width: 130 }}>
                          <InputLabel>Visibility</InputLabel>
                          <Select
                            value={row.visibility}
                            label="Visibility"
                            onChange={e => updateFormatRow(idx, { visibility: e.target.value as MenuVisibility })}
                          >
                            <MenuItem value="LIVE">LIVE</MenuItem>
                            <MenuItem value="HIDDEN">HIDDEN</MenuItem>
                          </Select>
                        </FormControl>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeFormatRow(idx)}
                          aria-label="Remove serving size"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Line 2: per-serving stock & recipe (Phase 7.A.2a/2b/2c) */}
                      {row._id && menuId && itemId ? (
                        <FormatExecutionBlock
                          venueId={venueId}
                          menuId={menuId}
                          itemId={itemId}
                          formatId={row._id}
                          suggestedName={suggestedExecutionName(row.formatId)}
                          onLinkedItemChange={handleLinkedItemChange}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Save this item first. Then you can set up Stock &amp; recipe for this serving option.
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* Actions */}
          <Box sx={{ pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: 150 }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : isEdit ? 'Save Changes' : 'Add to Menu'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* CreateWineModal — opens from WineSearchBox's "+ Create new wine" sentinel */}
      <CreateWineModal
        open={createModalOpen}
        initialQuery={createModalQuery}
        onCreated={(wine: Wine) => {
          setSelectedWine(wineToSearchResult(wine));
          setErrors(prev => ({ ...prev, wine: undefined }));
          setCreateModalOpen(false);
        }}
        onClose={() => setCreateModalOpen(false)}
      />
    </Box>
  );
};

export default MenuItemFormWine;
